import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MultiImageUpload } from "./MultiImageUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface ProjectData {
  id?: string;
  project_name: string;
  description: string;
  technologies: string[];
  image_url: string;
  github_url: string;
  live_demo_url: string;
  display_order: number;
  category: string;
  featured: boolean;
  start_date: string;
  end_date: string;
  status: string;
  role: string;
  team_size: string;
  highlights: string[];
}

interface ProjectFormProps {
  initialData?: ProjectData;
  onSuccess?: () => void;
}

const PROJECT_CATEGORIES = [
  "Web Application",
  "Mobile App",
  "API / Backend",
  "Desktop Application",
  "Library / Package",
  "Design System",
  "Data Science",
  "Machine Learning",
  "DevOps / Infrastructure",
  "Other",
];

export const ProjectForm = ({ initialData, onSuccess }: ProjectFormProps) => {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState<string | null>(initialData?.id || null);
  const [formData, setFormData] = useState({
    project_name: "",
    description: "",
    technologies: "",
    image_url: "",
    github_url: "",
    live_demo_url: "",
    display_order: 0,
    category: "",
    featured: false,
    start_date: "",
    end_date: "",
    status: "completed",
    role: "",
    team_size: "",
    highlights: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        project_name: initialData.project_name || "",
        description: initialData.description || "",
        technologies: initialData.technologies?.join(", ") || "",
        image_url: initialData.image_url || "",
        github_url: initialData.github_url || "",
        live_demo_url: initialData.live_demo_url || "",
        display_order: initialData.display_order || 0,
        category: initialData.category || "",
        featured: initialData.featured || false,
        start_date: initialData.start_date || "",
        end_date: initialData.end_date || "",
        status: initialData.status || "completed",
        role: initialData.role || "",
        team_size: initialData.team_size || "",
        highlights: initialData.highlights?.join("\n") || "",
      });
    }
  }, [initialData]);

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const techArray = data.technologies.split(",").map((t) => t.trim()).filter(Boolean);
      const highlightsArray = data.highlights.split("\n").map((h) => h.trim()).filter(Boolean);

      const projectData = {
        project_name: data.project_name,
        description: data.description || null,
        technologies: techArray.length > 0 ? techArray : null,
        image_url: data.image_url || null,
        github_url: data.github_url || null,
        live_demo_url: data.live_demo_url || null,
        display_order: data.display_order,
        category: data.category || null,
        featured: data.featured,
        status: data.status || "completed",
        role: data.role || null,
        team_size: data.team_size || null,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        highlights: highlightsArray.length > 0 ? highlightsArray : null,
      };

      if (initialData?.id) {
        const { error } = await supabase
          .from("projects")
          .update(projectData)
          .eq("id", initialData.id);
        if (error) throw error;
        return initialData.id;
      } else {
        const { data: newProject, error } = await supabase
          .from("projects")
          .insert([projectData])
          .select()
          .single();
        if (error) throw error;
        return newProject.id;
      }
    },
    onSuccess: (newProjectId) => {
      queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success(initialData ? "Project updated successfully" : "Project added successfully");

      if (!initialData) {
        setProjectId(newProjectId);
        setFormData({
          project_name: "",
          description: "",
          technologies: "",
          image_url: "",
          github_url: "",
          live_demo_url: "",
          display_order: 0,
          category: "",
          featured: false,
          start_date: "",
          end_date: "",
          status: "completed",
          role: "",
          team_size: "",
          highlights: "",
        });
      }

      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="project_name">Project Name *</Label>
            <Input
              id="project_name"
              value={formData.project_name}
              onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            placeholder="Describe the project, its purpose, and key features..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="technologies">Technologies (comma-separated)</Label>
          <Input
            id="technologies"
            value={formData.technologies}
            onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
            placeholder="React, TypeScript, Tailwind CSS, Node.js"
          />
        </div>
      </div>

      {/* Project Details */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Project Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="role">Your Role</Label>
            <Input
              id="role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="Lead Developer, Full Stack Developer, etc."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="team_size">Team Size</Label>
            <Input
              id="team_size"
              value={formData.team_size}
              onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
              placeholder="Solo, 2-3, 5+, etc."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="start_date">Start Date</Label>
            <Input
              id="start_date"
              type="month"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="end_date">End Date</Label>
            <Input
              id="end_date"
              type="month"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              placeholder="Leave empty if ongoing"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="highlights">Key Highlights (one per line)</Label>
          <Textarea
            id="highlights"
            value={formData.highlights}
            onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
            rows={3}
            placeholder="Improved performance by 50%&#10;Implemented real-time features&#10;Reduced load time by 2 seconds"
          />
        </div>
      </div>

      {/* Links */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Links</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="github_url">GitHub Repository URL</Label>
            <Input
              id="github_url"
              type="url"
              value={formData.github_url}
              onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
              placeholder="https://github.com/username/repo"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="live_demo_url">Live Demo URL</Label>
            <Input
              id="live_demo_url"
              type="url"
              value={formData.live_demo_url}
              onChange={(e) => setFormData({ ...formData, live_demo_url: e.target.value })}
              placeholder="https://example.com"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="image_url">Legacy Image URL (optional)</Label>
          <Input
            id="image_url"
            type="url"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="https://example.com/project-image.jpg"
          />
          <p className="text-xs text-muted-foreground">
            Use the multi-image upload below for better image management
          </p>
        </div>
      </div>

      {/* Display Options */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Display Options</h3>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="featured">Featured Project</Label>
            <p className="text-xs text-muted-foreground">
              Featured projects are highlighted on the resume
            </p>
          </div>
          <Switch
            id="featured"
            checked={formData.featured}
            onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="display_order">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            className="w-24"
          />
        </div>
      </div>

      <Button type="submit" disabled={mutation.isPending} className="w-full">
        {mutation.isPending ? (initialData ? "Updating..." : "Adding...") : (initialData ? "Update Project" : "Add Project")}
      </Button>

      {/* Multi-image upload section */}
      {(projectId || initialData?.id) && (
        <div className="pt-6 border-t border-border">
          <MultiImageUpload projectId={projectId || initialData.id} maxImages={5} />
        </div>
      )}
    </form>
  );
};
